import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink, FileText, Pickaxe, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';
import { api, errorMessage, timeAgo, useQuery } from './lib';
import {
  Btn, CopyButton, EmptyState, ErrorState, Page, Pagination, Panel, Pill, RefreshButton, RowsSkeleton,
  Segmented, StatCard, StatGrid, usePagination,
} from './ui';

const SUBREDDITS = ['relationships', 'Marriage', 'selfimprovement', 'DecidingToBeBetter', 'datingoverthirty', 'AskMen'];

const MODES = {
  drafts: {
    title: 'Story miner',
    intro: 'Finds real stories and turns them into Reddit-native post drafts, with a comment to seed and a soft reply.',
    statuses: [['pending', 'To post', 'warning'], ['posted', 'Posted', 'success'], ['archived', 'Archived', 'neutral']],
    path: '/admin/reddit/drafts',
    listKey: 'drafts',
  },
  leads: {
    title: 'Lead finder',
    intro: 'Searches Reddit for men asking for a tool or a system to be a better husband, and drafts a reply for each post.',
    statuses: [['new', 'New', 'warning'], ['replied', 'Replied', 'success'], ['skipped', 'Skipped', 'neutral']],
    path: '/admin/reddit/leads',
    listKey: 'leads',
  },
};

function TextBlock({ label, text }) {
  if (!text) return null;
  return (
    <div className="rounded-lg border border-border bg-secondary/40 p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <CopyButton text={text} label="Copy" />
      </div>
      <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground">{text}</p>
    </div>
  );
}

function StatusActions({ mode, item, onStatus }) {
  const statuses = MODES[mode].statuses;
  return (
    <div className="flex flex-wrap gap-1">
      {statuses.filter(([value]) => value !== item.status).map(([value, label]) => (
        <Btn key={value} variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onStatus(item, value)}>
          Mark {label.toLowerCase()}
        </Btn>
      ))}
    </div>
  );
}

function DraftCard({ draft, onStatus }) {
  const [expanded, setExpanded] = useState(false);
  const status = MODES.drafts.statuses.find(([v]) => v === draft.status);
  return (
    <article className="space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Pill>r/{draft.subreddit}</Pill>
          {status && <Pill tone={status[2]}>{status[1]}</Pill>}
          {draft.created_at && <span className="text-xs text-muted-foreground">{timeAgo(draft.created_at)}</span>}
        </div>
        <StatusActions mode="drafts" item={draft} onStatus={onStatus} />
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">{draft.title}</p>
        <p className={cn('mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-muted-foreground', !expanded && 'line-clamp-4')}>
          {draft.body}
        </p>
        {(draft.body || '').length > 300 && (
          <button type="button" className="mt-1 text-xs font-medium text-foreground underline-offset-2 hover:underline" onClick={() => setExpanded((e) => !e)}>
            {expanded ? 'Show less' : 'Show all'}
          </button>
        )}
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <TextBlock label="Comment to seed" text={draft.comment_seed} />
        <TextBlock label="Soft reply" text={draft.soft_reply} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <CopyButton text={`${draft.title}\n\n${draft.body}`} label="Copy post" className="h-8 border border-border px-3" />
        {draft.source_url && (
          <Btn variant="ghost" size="sm" asChild>
            <a href={draft.source_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5" />Source story</a>
          </Btn>
        )}
      </div>
    </article>
  );
}

function LeadCard({ lead, onStatus }) {
  const status = MODES.leads.statuses.find(([v]) => v === lead.status);
  return (
    <article className="space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {lead.subreddit && <Pill>r/{lead.subreddit}</Pill>}
          {status && <Pill tone={status[2]}>{status[1]}</Pill>}
          {lead.score > 0 && <Pill tone="accent">{Math.round(lead.score * 100)}% match</Pill>}
          {lead.created_at && <span className="text-xs text-muted-foreground">{timeAgo(lead.created_at)}</span>}
        </div>
        <StatusActions mode="leads" item={lead} onStatus={onStatus} />
      </div>
      <div>
        <a href={lead.post_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-1 text-sm font-semibold text-foreground hover:underline">
          {lead.post_title}<ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        </a>
        {lead.snippet && <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{lead.snippet}</p>}
      </div>
      <TextBlock label="Suggested reply" text={lead.suggested_reply} />
    </article>
  );
}

export default function RedditPanel() {
  const [mode, setMode] = useState('drafts');
  const config = MODES[mode];
  const { data, error, loading, refetch, updatedAt, setData } = useQuery(
    `reddit:${mode}`,
    async () => (await api.get(config.path))[config.listKey] || [],
  );
  const [filter, setFilter] = useState('all');
  const [count, setCount] = useState(3);
  const [subs, setSubs] = useState(['relationships', 'Marriage', 'selfimprovement']);
  const [running, setRunning] = useState(false);

  const list = useMemo(() => data || [], [data]);
  const counts = useMemo(() => {
    const out = { all: list.length };
    config.statuses.forEach(([value]) => { out[value] = list.filter((i) => i.status === value).length; });
    return out;
  }, [list, config]);
  const filtered = filter === 'all' ? list : list.filter((i) => i.status === filter);
  const pager = usePagination(filtered, 10, `${mode}|${filter}`);

  const switchMode = (next) => {
    setMode(next);
    setFilter('all');
  };

  const run = async () => {
    setRunning(true);
    try {
      if (mode === 'drafts') {
        const res = await api.post('/admin/reddit/mine', { count, subreddits: subs });
        const s = res.stats || {};
        toast.success(`${s.posts_generated || 0} new draft${s.posts_generated === 1 ? '' : 's'} from ${s.stories_found || 0} stories`);
      } else {
        const res = await api.post('/admin/reddit/leads/find', { count });
        const s = res.stats || {};
        const msg = `${s.searched || 0} searches → ${s.found || 0} posts → ${s.qualified || 0} matched → ${s.saved || 0} new leads`;
        if (s.saved) toast.success(msg);
        else toast.message(`${msg}. Try more searches.`);
      }
      refetch();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  const setStatus = async (item, status) => {
    const previous = item.status;
    setData((all) => (all || []).map((i) => (i.id === item.id ? { ...i, status } : i)));
    try {
      await api.patch(`${config.path}/${item.id}`, { status });
    } catch (err) {
      setData((all) => (all || []).map((i) => (i.id === item.id ? { ...i, status: previous } : i)));
      toast.error(errorMessage(err));
    }
  };

  const toggleSub = (s) => setSubs((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <Page
      title="Reddit"
      description="Tools for posting on Reddit and finding people who need the app."
      actions={(
        <>
          <Segmented
            size="md"
            value={mode}
            onChange={switchMode}
            options={[{ value: 'drafts', label: 'Story miner' }, { value: 'leads', label: 'Lead finder' }]}
          />
          <RefreshButton onClick={refetch} loading={loading} updatedAt={updatedAt} />
        </>
      )}
    >
      <StatGrid className="lg:grid-cols-4">
        <StatCard label={mode === 'drafts' ? 'Drafts' : 'Leads'} value={counts.all} loading={!data && loading} />
        {config.statuses.map(([value, label]) => (
          <StatCard
            key={value}
            label={label}
            value={counts[value] ?? 0}
            tone={(value === 'pending' || value === 'new') && counts[value] ? 'warning' : undefined}
            loading={!data && loading}
          />
        ))}
      </StatGrid>

      <Panel title={mode === 'drafts' ? 'Mine new stories' : 'Find new leads'} description={config.intro}>
        <div className="space-y-4">
          {mode === 'drafts' && (
            <div>
              <p className="mb-2 text-xs text-muted-foreground">Subreddits to draw stories from</p>
              <div className="flex flex-wrap gap-1.5">
                {SUBREDDITS.map((s) => {
                  const on = subs.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSub(s)}
                      aria-pressed={on}
                      className={cn(
                        'h-7 rounded-full border px-3 text-xs font-medium transition-colors',
                        on ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:text-foreground',
                      )}
                    >
                      r/{s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="max-w-md">
            <div className="mb-2 flex justify-between text-xs">
              <span className="text-muted-foreground">{mode === 'drafts' ? 'Stories to turn into drafts' : 'Searches to run'}</span>
              <span className="font-medium tabular">{count}</span>
            </div>
            <Slider value={[count]} onValueChange={([v]) => setCount(v)} min={1} max={10} step={1} />
          </div>
          <Btn onClick={run} disabled={running || (mode === 'drafts' && subs.length === 0)}>
            {mode === 'drafts' ? <Pickaxe className="h-4 w-4" /> : <Search className="h-4 w-4" />}
            {running ? 'Working… this can take a minute' : mode === 'drafts' ? `Mine ${count} ${count === 1 ? 'story' : 'stories'}` : `Run ${count} ${count === 1 ? 'search' : 'searches'}`}
          </Btn>
        </div>
      </Panel>

      {error && <ErrorState error={error} onRetry={refetch} />}

      <Panel flush>
        <div className="border-b border-border p-3">
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[{ value: 'all', label: 'All', count: counts.all }, ...config.statuses.map(([value, label]) => ({ value, label, count: counts[value] }))]}
          />
        </div>
        {!data && loading ? (
          <RowsSkeleton rows={4} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={mode === 'drafts' ? FileText : Search}
            title={list.length ? 'Nothing with this status' : mode === 'drafts' ? 'No drafts yet' : 'No leads yet'}
            description={list.length ? undefined : 'Run the tool above to create some.'}
          />
        ) : (
          <>
            <div className="divide-y divide-border">
              {pager.slice.map((item) => (mode === 'drafts'
                ? <DraftCard key={item.id} draft={item} onStatus={setStatus} />
                : <LeadCard key={item.id} lead={item} onStatus={setStatus} />))}
            </div>
            <Pagination {...pager} noun={mode === 'drafts' ? 'drafts' : 'leads'} />
          </>
        )}
      </Panel>
    </Page>
  );
}
